import type {
  CompiledQueryState,
  CompileQuerySessionRequest,
  DescribeTypesRequest,
  ExecuteQueryRequest,
  FacetRequest,
  FacetResult,
  PreviewFilterRequest,
  QueryPreviewResult,
  QueryResultPage,
  QueryToolbarRuntime,
  ReferenceLookupRequest,
  ReferenceLookupResult,
} from '@aeppic/query/contracts'
import type * as Types from '@aeppic/types'
import { Scopes } from '../../model/query/index.js'

import { compileSessionExpressions } from './compiler.js'
import { collectFacetItems, createFacetSession, facetProjection, findFacetField } from './facets.js'
import { expandSessionFragments } from './fragments.js'
import { describeDocumentTypes } from './metadata.js'
import { compileSessionWithoutRelations, materializeSessionRelations, sessionContainsRelations } from './relations.js'
import type { QueryToolbarAeppicApi, QueryToolbarRuntimeOptions } from './types.js'
import { validateFilterExpression } from './validation.js'

export type { QueryToolbarAeppicApi, QueryToolbarRuntimeOptions } from './types.js'

const DEFAULT_MAX_EXPRESSION_DEPTH = 32
const DEFAULT_MAX_FRAGMENT_DEPTH = 16
const DEFAULT_FACET_DOCUMENT_LIMIT = 5000
const DEFAULT_RELATION_MATERIALIZATION_LIMIT = 5000
const DEFAULT_RELATION_PAGE_SIZE = 100

export function createQueryToolbarRuntime(aeppic: QueryToolbarAeppicApi, options: QueryToolbarRuntimeOptions = {}): QueryToolbarRuntime {
  return new AeppicQueryToolbarRuntime(aeppic, options)
}

class AeppicQueryToolbarRuntime implements QueryToolbarRuntime {
  private readonly aeppic: QueryToolbarAeppicApi
  private readonly resolveNamedFragment: QueryToolbarRuntimeOptions['resolveNamedFragment']
  private readonly maxExpressionDepth: number
  private readonly maxFragmentDepth: number
  private readonly facetDocumentLimit: number
  private readonly relationMaterializationLimit: number
  private readonly relationPageSize: number

  constructor(aeppic: QueryToolbarAeppicApi, options: QueryToolbarRuntimeOptions) {
    this.aeppic = aeppic
    this.resolveNamedFragment = options.resolveNamedFragment
    this.maxExpressionDepth = options.maxExpressionDepth ?? DEFAULT_MAX_EXPRESSION_DEPTH
    this.maxFragmentDepth = Math.max(1, options.maxFragmentDepth ?? DEFAULT_MAX_FRAGMENT_DEPTH)
    this.facetDocumentLimit = Math.max(1, options.facetDocumentLimit ?? DEFAULT_FACET_DOCUMENT_LIMIT)
    this.relationMaterializationLimit = Math.max(1, options.relationMaterializationLimit ?? DEFAULT_RELATION_MATERIALIZATION_LIMIT)
    this.relationPageSize = Math.max(1, options.relationPageSize ?? DEFAULT_RELATION_PAGE_SIZE)
  }

  async describeTypes(request: DescribeTypesRequest) {
    return describeDocumentTypes(this.aeppic, request.types)
  }

  async compile(request: CompileQuerySessionRequest): Promise<CompiledQueryState> {
    throwIfAborted(request.signal)
    const diagnostics = [
      ...validateOptionalExpression(request.session.search, ['search'], this.maxExpressionDepth),
      ...validateOptionalExpression(request.session.toolbarFilter, ['toolbarFilter'], this.maxExpressionDepth),
      ...Object.keys(request.session.contributions).sort().flatMap(key => validateOptionalExpression(request.session.contributions[key], ['contributions', key], this.maxExpressionDepth)),
    ]

    if (diagnostics.some(diagnostic => diagnostic.severity === 'error')) {
      return {
        session: request.session,
        candidateQuery: request.session.baseQuery,
        executionMode: 'query',
        diagnostics,
      }
    }

    const descriptors = await this.describeTypes({ types: request.session.documentTypes })
    throwIfAborted(request.signal)
    const expanded = await expandSessionFragments(
      request.session,
      this.resolveNamedFragment,
      this.maxFragmentDepth,
      request.signal,
    )
    if (expanded.diagnostics.some(diagnostic => diagnostic.severity === 'error')) {
      return {
        session: request.session,
        candidateQuery: request.session.baseQuery,
        executionMode: 'query',
        diagnostics: expanded.diagnostics,
      }
    }
    if (request.relationResolution === 'preserve' && sessionContainsRelations(expanded.session)) {
      const candidate = compileSessionWithoutRelations(expanded.session, descriptors)
      const preserveDiagnostics = [...expanded.diagnostics, ...candidate.diagnostics]
      return {
        session: request.session,
        candidateQuery: candidate.query ?? request.session.baseQuery,
        executionMode: 'orchestrated',
        diagnostics: preserveDiagnostics,
        materializations: [],
      }
    }
    const materialized = await materializeSessionRelations(
      this.aeppic,
      expanded.session,
      descriptors,
      this.relationMaterializationLimit,
      this.relationPageSize,
      request.signal,
    )
    const compiled = compileSessionExpressions(materialized.session, descriptors)
    const compileDiagnostics = [...expanded.diagnostics, ...materialized.diagnostics, ...compiled.diagnostics]
    const incompleteMaterialization = materialized.materializations.some(materialization => !materialization.complete)

    if (!compiled.query || compileDiagnostics.some(diagnostic => diagnostic.severity === 'error')) {
      return {
        session: request.session,
        candidateQuery: materialized.candidateQuery,
        executionMode: incompleteMaterialization ? 'orchestrated' : 'query',
        diagnostics: compileDiagnostics,
        materializations: materialized.materializations,
      }
    }

    return {
      session: request.session,
      candidateQuery: compiled.query,
      effectiveQuery: compiled.query,
      executionMode: 'query',
      diagnostics: compileDiagnostics,
      materializations: materialized.materializations,
    }
  }

  async execute(request: ExecuteQueryRequest): Promise<QueryResultPage> {
    throwIfAborted(request.signal)
    if (!request.compiled.effectiveQuery) {
      throw new Error('Cannot execute a compiled query without an effectiveQuery')
    }

    const page = request.page ?? 0
    const pageSize = request.pageSize ?? 100
    const documents = await this.aeppic.find(request.compiled.effectiveQuery, {
      start: page * pageSize,
      size: pageSize + 1,
    })
    throwIfAborted(request.signal)

    return {
      documents: documents.slice(0, pageSize),
      page,
      pageSize,
      hasMore: documents.length > pageSize,
    }
  }

  async lookupValues(request: FacetRequest): Promise<FacetResult> {
    throwIfAborted(request.signal)
    const descriptors = await this.describeTypes({ types: request.session.documentTypes })
    const descriptor = findFacetField(descriptors, request.field)
    if (!descriptor) {
      return {
        field: request.field,
        items: [],
        complete: false,
        diagnostics: [{ code: 'unknown-facet-field', message: `Unknown facet field ${request.field}`, severity: 'error', path: ['field'] }],
      }
    }

    const session = createFacetSession(request.session, request.field, request.excludeContributionKey)
    const compiled = await this.compile({ session, signal: request.signal })
    if (!compiled.effectiveQuery) {
      return { field: request.field, items: [], complete: false, diagnostics: compiled.diagnostics }
    }

    const documents = await this.aeppic.find(compiled.effectiveQuery, {
      start: 0,
      size: this.facetDocumentLimit + 1,
      fields: facetProjection(request.field),
    })
    throwIfAborted(request.signal)

    const sourceComplete = documents.length <= this.facetDocumentLimit
    const items = collectFacetItems(documents.slice(0, this.facetDocumentLimit), descriptor, sourceComplete)
    const itemLimit = request.limit == null ? items.length : Math.max(0, request.limit)
    const valuesComplete = items.length <= itemLimit
    const diagnostics = [...compiled.diagnostics]

    if (!sourceComplete) {
      diagnostics.push({
        code: 'facet-source-truncated',
        message: `Facet counts are lower bounds because more than ${this.facetDocumentLimit} documents match`,
        severity: 'warning',
      })
    }
    if (!valuesComplete) {
      diagnostics.push({
        code: 'facet-values-truncated',
        message: `Facet values were limited to ${itemLimit} items`,
        severity: 'warning',
      })
    }

    return {
      field: request.field,
      items: items.slice(0, itemLimit),
      complete: sourceComplete && valuesComplete,
      diagnostics,
    }
  }

  async lookupReferences(request: ReferenceLookupRequest): Promise<ReferenceLookupResult> {
    throwIfAborted(request.signal)
    const targetTypes = request.sources
      ? request.sources.flatMap(source => source.targetTypes)
      : [...request.targetTypes]
    if (targetTypes.length === 0) {
      return {
        items: [],
        complete: true,
        diagnostics: [{ code: 'missing-reference-target-type', message: 'Reference lookup requires at least one target type', severity: 'error', path: ['targetTypes'] }],
      }
    }

    const descriptors = await this.describeTypes({ types: targetTypes })
    const text = String(request.text ?? '').trim()
    const session = {
      baseQuery: request.sources
        ? referenceSourceQuery(request.sources)
        : Scopes.global().forms([...new Set(targetTypes.map(type => type.id))]).toQuery(),
      documentTypes: targetTypes,
      search: text ? { kind: 'text-search' as const, text, mode: 'contains' as const, combine: 'AND' as const } : null,
      toolbarFilter: null,
      contributions: {},
    }
    const compiled = compileSessionExpressions(session, descriptors)
    if (!compiled.query || compiled.diagnostics.some(diagnostic => diagnostic.severity === 'error')) {
      return { items: [], complete: false, diagnostics: compiled.diagnostics }
    }

    const limit = Math.max(1, request.limit ?? 20)
    const documents = await this.aeppic.find(compiled.query, { start: 0, size: limit + 1, fields: ['name', 'label'] })
    throwIfAborted(request.signal)
    const seen = new Set<string>()
    const items = documents.slice(0, limit).flatMap(document => {
      if (!document.id || seen.has(document.id)) return []
      seen.add(document.id)
      const label = readReferenceLabel(document)
      return [{
        id: document.id,
        label,
        ...(document.f?.id ? { documentType: { id: document.f.id, ...(document.f.v ? { version: document.f.v } : {}) } } : {}),
      }]
    })

    return {
      items,
      complete: documents.length <= limit,
      diagnostics: compiled.diagnostics,
    }
  }

  async preview(_request: PreviewFilterRequest): Promise<QueryPreviewResult> {
    return {
      documents: [],
      complete: false,
      diagnostics: [{ code: 'unsupported-preview', message: 'Filter preview is not available in the Checkpoint A runtime', severity: 'info' }],
    }
  }
}

function readReferenceLabel(document: Types.Document): string {
  const label = document.data?.name ?? document.data?.label ?? document.f?.text ?? document.id
  return String(label ?? '')
}

function validateOptionalExpression(expression, path: readonly (string | number)[], maxDepth: number) {
  return expression ? validateFilterExpression(expression, path, maxDepth) : []
}

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) {
    throw new Error('Query toolbar operation aborted')
  }
}

function referenceSourceQuery(sources: NonNullable<ReferenceLookupRequest['sources']>) {
  const builder = Scopes.filter()
  for (const source of sources) {
    if (source.targetTypes.length === 0) {
      continue
    }
    builder.or.and(source.query)
  }
  return builder.toQuery()
}
